import { useEffect, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useReducedMotion } from 'motion/react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

/** Entrada de cada bloco marcado com data-reveal. */
export function useReveal(scope: RefObject<HTMLElement | null>) {
  const reduce = useReducedMotion();
  useGSAP(() => {
    if (reduce) return;
    for (const element of gsap.utils.toArray<HTMLElement>('[data-reveal]', scope.current)) {
      gsap.from(element, { y: 26, autoAlpha: 0, duration: .7, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 94%' } });
    }
    ScrollTrigger.refresh();
  }, { scope, dependencies: [reduce] });
}

/** Blobs do fundo: flutuação contínua + parallax no scroll. */
export function useBlobs(scope: RefObject<HTMLElement | null>) {
  const reduce = useReducedMotion();
  useGSAP(() => {
    if (reduce) return;
    gsap.utils.toArray<HTMLElement>('.blob', scope.current).forEach((blob, index) => {
      gsap.to(blob, { xPercent: index % 2 ? -10 : 10, yPercent: -8, duration: 9 + index * 2, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to(blob, { y: 220 + index * 60, ease: 'none', scrollTrigger: { trigger: scope.current, start: 'top top', end: 'bottom top', scrub: .6 } });
    });
  }, { scope, dependencies: [reduce] });
}

/** Hover dos tiles do hub: o ícone salta e a descrição se abre. */
export function useToolHover(scope: RefObject<HTMLElement | null>) {
  const reduce = useReducedMotion();
  useGSAP(() => {
    if (reduce || !window.matchMedia('(hover: hover)').matches) return;
    const off: (() => void)[] = [];
    for (const tool of gsap.utils.toArray<HTMLElement>('.tool', scope.current)) {
      const icon = tool.querySelector('.tool-icon');
      const desc = tool.querySelector('.tool-desc');
      const enter = () => {
        gsap.to(icon, { y: -4, scale: 1.12, rotate: -6, duration: .45, ease: 'back.out(2)' });
        gsap.to(desc, { height: 'auto', opacity: 1, duration: .4 });
      };
      const leave = () => {
        gsap.to(icon, { y: 0, scale: 1, rotate: 0, duration: .4, ease: 'power3.out' });
        gsap.to(desc, { height: 0, opacity: 0, duration: .3 });
      };
      tool.addEventListener('mouseenter', enter);
      tool.addEventListener('mouseleave', leave);
      off.push(() => { tool.removeEventListener('mouseenter', enter); tool.removeEventListener('mouseleave', leave); });
    }
    return () => { for (const remove of off) remove(); };
  }, { scope, dependencies: [reduce] });
}

/** Brilho que acompanha o mouse no card principal do hub. */
export function useSpotlight(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const onMove = (event: MouseEvent) => {
      const box = element.getBoundingClientRect();
      element.style.setProperty('--mx', `${((event.clientX - box.left) / box.width) * 100}%`);
      element.style.setProperty('--my', `${((event.clientY - box.top) / box.height) * 100}%`);
    };
    element.addEventListener('mousemove', onMove);
    return () => element.removeEventListener('mousemove', onMove);
  }, [ref]);
}


export { gsap, ScrollTrigger };
